import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';

const useStyles = makeStyles({
  root: {
    position: 'fixed',
    bottom: 16,
    left: 16,
    zIndex: 10000,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    color: '#fff',
    padding: '10px 12px',
    borderRadius: 4,
    fontSize: 12,
    lineHeight: 1.4,
    maxWidth: 280,
    pointerEvents: 'auto',
  },
  title: {
    fontWeight: 600,
    marginBottom: 8,
  },
  button: {
    color: '#fff',
    borderColor: 'rgba(255, 255, 255, 0.5)',
    fontSize: 12,
    textTransform: 'none',
    minHeight: 0,
    minWidth: 0,
    '&:hover': {
      borderColor: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '& .MuiButton-label': {
      padding: '2px 8px',
    },
  },
  status: {
    marginTop: 8,
    opacity: 0.85,
    wordBreak: 'break-word',
  },
});

const isDebugModeEnabled = () => new URLSearchParams(window.location.search).get('debug') === '1';

const serializeStoreState = (state: unknown): string => {
  const seen = new WeakSet();
  return JSON.stringify(
    state,
    (_key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }
      return value;
    },
    2
  );
};

export const DebugBox = () => {
  const classes = useStyles();
  const enabled = useMemo(() => isDebugModeEnabled(), []);
  const [status, setStatus] = useState<string | null>(null);

  const downloadStoreDump = useCallback(() => {
    setStatus(null);
    try {
      const state = Services.store?.state;
      if (!state) {
        setStatus('Store not available yet.');
        return;
      }
      const json = serializeStoreState(state);
      const filename = `store_dump_${Date.now()}.json`;
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      const kb = (blob.size / 1024).toFixed(1);
      setStatus(`Downloaded ${filename} (${kb} KB).`);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Download failed.');
    }
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <div className={classes.root} role="region" aria-label="Debug tools">
      <div className={classes.title}>Debug tools</div>
      <Button variant="outlined" size="small" className={classes.button} onClick={downloadStoreDump}>
        Download store dump
      </Button>
      {status && <div className={classes.status}>{status}</div>}
    </div>
  );
};

export default DebugBox;
